<template>
    <header class="sticky top-0 z-30 h-[80px] md:h-[96px]" :class="[hasNavigation ? 'bg-transparent' : 'bg-transparent', { 'bg-white': isScrolled }]">
        <!-- Full-width navigation island -->
        <div v-if="hasNavigation" ref="navigationContainer" class="absolute inset-0 flex items-center justify-center z-10 hidden md:flex">
            <div
                ref="navigationIsland"
                class="navigation-island bg-white border border-black border-opacity-10 w-full flex items-center justify-center"
                :class="{
                    'no-navigation': !hasNavigation,
                    'has-navigation': hasNavigation,
                    expanded: isScrolled
                }"
                :style="islandStyle"
            >
                <!-- Content container with max-width constraint -->
                <div class="max-w-[1320px] w-full mx-4 flex items-center justify-center header-nav-container">
                    <div class="flex items-center navigation-content relative">
                        <div ref="activeIndicator" class="absolute rounded-md" style="background-color: #000" :style="indicatorStyle" />

                        <button
                            v-for="(option, index) in navigationOptions"
                            :key="option.id"
                            :ref="(el) => el && (navButtons[index] = el as HTMLElement)"
                            class="py-1 px-3 text-sm font-medium rounded-md h-8 mx-1 whitespace-nowrap relative z-10 transition-colors duration-200 flex items-center justify-center"
                            :class="[activeTab === option.id ? 'text-white' : 'text-gray-700 hover:bg-gray-100']"
                            @click="handleTabChange(option.id)"
                        >
                            {{ option.label }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Header content container -->
        <div class="max-w-[1320px] relative h-full header-content-container">
            <div class="absolute inset-x-0 flex items-center justify-between h-[80px] md:h-[96px] py-3 z-20 pointer-events-none">
                <div class="flex items-end pointer-events-auto">
                    <img :src="mitLogo" :alt="`${appName} Logo`" class="h-11 w-auto cursor-pointer pr-[4px]" @click="() => emit('navigate-home')" />
                    <div class="flex flex-col justify-between h-10">
                        <span class="h-5 !text-[17px] !font-semibold !leading-none">Architecture</span>
                        <span class="h-5 !text-[17px] !font-semibold !leading-none">Dashboard</span>
                    </div>
                </div>

                <div class="hidden md:flex items-center space-x-2 pointer-events-auto">
                    <slot name="desktop-actions">
                        <!-- Chat icon -->
                        <!-- <button class="p-2 rounded-full flex items-center justify-center hover:!scale-110">
                            <svg width="30" height="30" viewBox="0 0 33 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M25.5 16.2344C25.5034 17.5543 25.1951 18.8563 24.6 20.0344C23.8944 21.4461 22.8098 22.6336 21.4674 23.4637C20.1251 24.2938 18.5782 24.7338 17 24.7344C15.6801 24.7378 14.3781 24.4295 13.2 23.8344L7.5 25.7344L9.4 20.0344C8.80493 18.8563 8.49656 17.5543 8.5 16.2344C8.50061 14.6562 8.94061 13.1093 9.77072 11.767C10.6008 10.4247 11.7883 9.33997 13.2 8.6344C14.3781 8.03933 15.6801 7.73096 17 7.7344H17.5C19.5843 7.8494 21.553 8.72916 23.0291 10.2053C24.5052 11.6814 25.385 13.6501 25.5 15.7344V16.2344Z"
                                    fill="url(#paint0_linear_14770_149532)"
                                    stroke="url(#paint1_linear_14770_149532)"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M17.1087 17.2078C17.4358 17.2078 17.7009 16.9427 17.7009 16.6156C17.7009 16.2886 17.4358 16.0234 17.1087 16.0234C16.7817 16.0234 16.5165 16.2886 16.5165 16.6156C16.5165 16.9427 16.7817 17.2078 17.1087 17.2078Z"
                                    stroke="white"
                                    stroke-width="1.28542"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M21.2541 17.2078C21.5812 17.2078 21.8464 16.9427 21.8464 16.6156C21.8464 16.2886 21.5812 16.0234 21.2541 16.0234C20.9271 16.0234 20.6619 16.2886 20.6619 16.6156C20.6619 16.9427 20.9271 17.2078 21.2541 17.2078Z"
                                    stroke="white"
                                    stroke-width="1.28542"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M12.9633 17.2078C13.2904 17.2078 13.5555 16.9427 13.5555 16.6156C13.5555 16.2886 13.2904 16.0234 12.9633 16.0234C12.6362 16.0234 12.3711 16.2886 12.3711 16.6156C12.3711 16.9427 12.6362 17.2078 12.9633 17.2078Z"
                                    stroke="white"
                                    stroke-width="1.28542"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient id="paint0_linear_14770_149532" x1="16.5" y1="1.73437" x2="15.45" y2="28.6744" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#FB26FF" />
                                        <stop offset="1" stop-color="#2864FF" />
                                    </linearGradient>
                                    <linearGradient id="paint1_linear_14770_149532" x1="16.5" y1="1.73437" x2="15.45" y2="28.6744" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#FB26FF" />
                                        <stop offset="1" stop-color="#2864FF" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </button> -->
                        <!-- App launcher -->
                        <!-- <button class="p-2 rounded-full flex items-center justify-center hover:!scale-110" @click="() => emit('toggle-app-launcher')">
                            <svg width="20" height="20" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M6.5 6.20312C6.5 7.09961 5.76953 7.79688 4.90625 7.79688H1.71875C0.822266 7.79688 0.125 7.09961 0.125 6.20312V3.01562C0.125 2.15234 0.822266 1.42188 1.71875 1.42188H4.90625C5.76953 1.42188 6.5 2.15234 6.5 3.01562V6.20312ZM6.5 14.7031C6.5 15.5996 5.76953 16.2969 4.90625 16.2969H1.71875C0.822266 16.2969 0.125 15.5996 0.125 14.7031V11.5156C0.125 10.6523 0.822266 9.92188 1.71875 9.92188H4.90625C5.76953 9.92188 6.5 10.6523 6.5 11.5156V14.7031ZM8.625 3.01562C8.625 2.15234 9.32227 1.42188 10.2188 1.42188H13.4062C14.2695 1.42188 15 2.15234 15 3.01562V6.20312C15 7.09961 14.2695 7.79688 13.4062 7.79688H10.2188C9.32227 7.79688 8.625 7.09961 8.625 6.20312V3.01562ZM15 14.7031C15 15.5996 14.2695 16.2969 13.4062 16.2969H10.2188C9.32227 16.2969 8.625 15.5996 8.625 14.7031V11.5156C8.625 10.6523 9.32227 9.92188 10.2188 9.92188H13.4062C14.2695 9.92188 15 10.6523 15 11.5156V14.7031Z"
                                    fill="#212121"
                                />
                            </svg>
                        </button> -->
                        <!-- Profile -->
                        <div class="relative">
                            <button ref="profileButton" class="p-2 rounded-full overflow-hidden flex items-center justify-center hover:!scale-110" @click="toggleProfileMenu">
                                <img v-if="userAvatar" :src="userAvatar" alt="User Profile" class="h-8 w-8 object-cover rounded-full" />
                                <div v-else class="bg-black/10 h-8 w-8 object-cover rounded-full flex items-center justify-center animate-pulse">
                                    <i class="fa-solid fa-user"></i>
                                </div>
                            </button>
                            <Menu ref="profileMenu" :model="profileMenuItems" popup />
                        </div>
                    </slot>
                </div>

                <div class="flex md:hidden items-center space-x-2 pointer-events-auto">
                    <slot name="mobile-actions">
                        <button class="p-2 rounded-full flex items-center justify-center">
                            <!-- SVG omitted -->
                        </button>
                        <button class="p-2 rounded-full flex items-center justify-center" @click="() => emit('toggle-mobile-menu')">
                            <i class="pi pi-bars" />
                        </button>
                    </slot>
                </div>
            </div>
        </div>
    </header>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, type CSSProperties } from 'vue';
import Menu from 'primevue/menu';
import mitLogo from '@/assets/images/mit_logo_std_rgb_black.svg';

interface NavOption {
    id: string;
    label: string;
}

const props = defineProps<{
    appName: string;
    userAvatar?: string;
    navigationOptions: NavOption[];
    activeTab?: string;
    showBreadcrumbs?: boolean;
}>();

const emit = defineEmits<{
    (e: 'navigate-home'): void;
    (e: 'change-tab', tabId: string): void;
    (e: 'logout'): void;
    (e: 'toggle-app-launcher'): void;
    (e: 'toggle-mobile-menu'): void;
}>();

const isScrolled = ref(false);
const isMobile = ref(false);
const navigationIsland = ref<HTMLElement | null>(null);
const navigationContainer = ref<HTMLElement | null>(null);
const activeIndicator = ref<HTMLElement | null>(null);
const navButtons = ref<HTMLElement[]>([]);
const profileButton = ref<HTMLElement | null>(null);
const profileMenu = ref<any>(null);
const indicatorStyle = ref<CSSProperties>({
    width: '0px',
    height: '32px',
    left: '0px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: '#000'
});
const islandStyle = ref<CSSProperties>({});

const profileMenuItems = ref([
    {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        class: 'body-medium',
        command: () => {
            emit('logout');
        }
    }
]);

const scrollThreshold = 10;
let scrollTimeout: number | null = null;
const previousActiveTabId = ref<string | null>(null);

const hasNavigation = computed(() => props.navigationOptions.length > 0);

function checkMobile() {
    isMobile.value = window.innerWidth < 768;
}

function handleTabChange(tabId: string) {
    if (tabId === props.activeTab) return;
    previousActiveTabId.value = props.activeTab || null;
    emit('change-tab', tabId);
}

function toggleProfileMenu(event: Event) {
    if (profileMenu.value) {
        profileMenu.value.toggle(event);
    }
}

function positionIndicator(animate = true) {
    if (!activeIndicator.value || navButtons.value.length === 0) return;
    const idx = props.navigationOptions.findIndex((o) => o.id === props.activeTab);
    if (idx < 0 || !navButtons.value[idx]) return;

    const activeBtn = navButtons.value[idx];
    const prevIdx = previousActiveTabId.value ? props.navigationOptions.findIndex((o) => o.id === previousActiveTabId.value) : -1;
    const prevBtn = prevIdx >= 0 ? navButtons.value[prevIdx] : null;

    // update classes
    navButtons.value.forEach((btn, i) => {
        if (i === idx) {
            btn.classList.add('text-white');
            btn.classList.remove('text-gray-700', 'hover:bg-gray-100');
        } else {
            btn.classList.remove('text-white');
            btn.classList.add('text-gray-700', 'hover:bg-gray-100');
        }
    });

    const newPos: CSSProperties = {
        width: `${activeBtn.offsetWidth}px`,
        height: '32px',
        left: `${activeBtn.offsetLeft}px`,
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: '#000'
    };

    if (animate && prevBtn && navigationIsland.value) {
        const temp = document.createElement('div');
        Object.assign(temp.style, {
            position: 'absolute',
            zIndex: '5',
            transition: 'all 0.3s ease',
            width: `${prevBtn.offsetWidth}px`,
            left: `${prevBtn.offsetLeft}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            height: '32px',
            backgroundColor: '#000'
        });
        const navContent = navigationIsland.value.querySelector('.navigation-content') as HTMLElement;
        if (navContent) {
            navContent.appendChild(temp);
            // trigger move
            nextTick(() => {
                Object.assign(indicatorStyle.value, newPos);
                temp.style.width = newPos.width as string;
                temp.style.left = newPos.left as string;
                setTimeout(() => navContent.removeChild(temp), 300);
            });
        } else {
            Object.assign(indicatorStyle.value, newPos);
        }
    } else {
        Object.assign(indicatorStyle.value, newPos);
    }

    previousActiveTabId.value = null;
}

function updateIslandStyle() {
    if (!navigationIsland.value || !navigationContainer.value || isMobile.value) return;

    // Since the island is now full width, we only need to manage the transition effects
    if (isScrolled.value) {
        islandStyle.value = {
            transition: 'all 0.3s ease'
        };
    } else {
        islandStyle.value = {
            transition: 'all 0.3s ease'
        };
    }
}

function onScroll() {
    if (scrollTimeout !== null) return;
    scrollTimeout = window.requestAnimationFrame(() => {
        const sc = window.scrollY > scrollThreshold;
        if (sc !== isScrolled.value) {
            isScrolled.value = sc;
            updateIslandStyle();
        }
        scrollTimeout = null;
    });
}

function onResize() {
    checkMobile();
    if (!isMobile.value) {
        nextTick(() => {
            updateIslandStyle();
            positionIndicator(false);
        });
    }
}

watch(
    () => props.activeTab,
    (oldTab) => {
        if (!previousActiveTabId.value && oldTab) {
            previousActiveTabId.value = oldTab;
        }
        nextTick(() => positionIndicator(true));
    },
    { immediate: true }
);

watch(
    () => props.navigationOptions,
    () => {
        navButtons.value = new Array(props.navigationOptions.length);
        nextTick(() => {
            updateIslandStyle();
            positionIndicator(false);
        });
    },
    { deep: true, immediate: true }
);

watch(isMobile, () => {
    if (!isMobile.value) {
        nextTick(() => {
            updateIslandStyle();
            positionIndicator(false);
        });
    }
});

onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    navButtons.value = new Array(props.navigationOptions.length);
    checkMobile();
    nextTick(() => {
        isScrolled.value = window.scrollY > scrollThreshold;
        updateIslandStyle();
        positionIndicator(false);
    });
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
    if (scrollTimeout) window.cancelAnimationFrame(scrollTimeout);
});
</script>

<style scoped>
.navigation-island {
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    min-width: fit-content;
    overflow: hidden;
    position: absolute;
    height: 57px;
}
.navigation-island.expanded {
    border: none !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
    box-shadow: none !important;
    height: 100% !important;
    border-radius: 0 !important;
}
.navigation-island.no-navigation {
    border-color: transparent !important;
    box-shadow: none !important;
}
.navigation-island.no-navigation.expanded {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.navigation-content {
    height: 57px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}
button.hover\:bg-gray-100:hover:not(.text-white) {
    background-color: rgba(243, 244, 246, 1);
}
@media (max-width: 1320px) {
    .navigation-container {
        display: none;
    }
}
header {
    transition: background-color 0.3s ease;
}
.header-nav-container,
.header-content-container {
    margin-left: 16px;
    margin-right: 16px;
}
@media (min-width: 1352px) {
    .header-nav-container,
    .header-content-container {
        margin-left: auto;
        margin-right: auto;
    }
}
</style>
